// Global
import {
  GetStaticComponentProps,
  useComponentProps,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

// Components
import { useTheme } from 'lib/context/ThemeContext';
import { mapItemFieldResultsToObject } from 'lib/graphql/mappers/map-item-field-results-to-object';
import { mapSearchResults } from 'lib/graphql/mappers/map-search-results';
import { IntegratedGraphQlResult } from 'lib/graphql/types/integrated-graphql-result';
import { ItemFieldResult } from 'lib/graphql/types/item-field-result';
import { ItemSearchResults } from 'lib/graphql/types/item-search-results';
import { getEnum } from 'lib/utils/get-enum';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { BodyCopy } from 'src/helpers/BodyCopy';
import Component from 'src/helpers/Component/Component';
import { Eyebrow } from 'src/helpers/Eyebrow';
import Headline from 'src/helpers/Headline/Headline';
import {
  MediaPrimary,
  MediaPrimaryStaticProps,
  getMediaPrimaryStaticProps,
} from 'src/helpers/Media';
import SwatchCollection, {
  LayoutStyles,
  SwatchCollectionProps,
} from 'src/helpers/SwatchCollection/SwatchCollection';
import { PromoSwatchesTheme } from './PromoSwatches.theme';

export type PromoSwatchesProps =
  Feature.EnterpriseWeb.Enterprise.Components.Promo.PromoSwatches.PromoSwatches & {
    fields?: {
      children: SwatchCollectionProps[];
    };
  };

const PromoSwatches = (props: PromoSwatchesProps) => {
  const propFields = props?.fields?.data?.item ? mapFields(props?.fields)?.fields : props.fields;
  const layoutStyle = getEnum<LayoutStyles>(propFields?.layoutStyle) || 'side-by-side';
  const { themeData, themeName } = useTheme(PromoSwatchesTheme(layoutStyle));
  const componentProps = useComponentProps<MediaPrimaryStaticProps>(props.rendering.uid);

  let contentColumn;
  let imageColumn;

  switch (layoutStyle) {
    case 'side-by-side':
      contentColumn = 'col-span-12 ml:col-span-6 order-last ml:order-first';
      imageColumn = 'col-span-12 ml:col-span-6';
      break;

    case 'full-width':
      contentColumn = 'col-span-12 order-last';
      imageColumn = 'col-span-12';
      break;
  }

  return (
    <Component
      variant="lg"
      gap={classNames(
        themeName === 'aw' ? 'gap-y-s ml:gap-y-m gap-x-s' : 'gap-y-m ml:gap-y-ml gap-x-s'
      )}
      dataComponent="promo/promoswatches"
      {...props}
      fields={propFields}
    >
      <div className={contentColumn}>
        <Eyebrow {...props} fields={propFields} classes={themeData.classes.eyebrow} />
        <Headline {...props} fields={propFields} classes={themeData.classes.headline} />
        <BodyCopy {...props} fields={propFields} classes={themeData.classes.bodycopy} />
        {propFields?.children?.map((swatchCollection: SwatchCollectionProps, index: number) => {
          return (
            <SwatchCollection
              {...swatchCollection}
              layoutStyle={layoutStyle}
              key={'swatchcollection-' + index}
            />
          );
        })}
      </div>
      <div className={imageColumn}>
        <MediaPrimary {...props} fields={propFields} staticProps={componentProps?.mediaPrimary} />
      </div>
    </Component>
  );
};

type IntegratedGraphQl = IntegratedGraphQlResult<{
  item: {
    fields: ItemFieldResult[];
    children: ItemSearchResults<{
      fields: ItemFieldResult[];
    }>;
  };
}>;

const mapFields = (fieldData: IntegratedGraphQl) => {
  return {
    fields: {
      ...mapItemFieldResultsToObject(fieldData.data.item.fields),
      children: mapSearchResults(fieldData.data.item.children, (child) => {
        return {
          fields: {
            ...mapItemFieldResultsToObject(child.fields),
          },
        };
      }),
    },
  };
};

export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  if (rendering.fields === undefined || !('data' in rendering.fields)) {
    return {};
  }

  const fields = rendering.fields as unknown as IntegratedGraphQl;

  const result = mapFields(fields);

  const datasource = result as PromoSwatchesProps;

  const mediaStaticProps: MediaPrimaryStaticProps = {};

  if (!datasource) {
    return { ...result, ...mediaStaticProps };
  }

  mediaStaticProps.mediaPrimary = await getMediaPrimaryStaticProps(datasource);

  return { ...result, ...mediaStaticProps };
};

export default withDatasourceCheck()(PromoSwatches);
